import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { CONTACT, LOGO, LOGO_TEXT } from "../../data/content";

const NAV = [
  { label: "Servicios", href: "#servicios" },
  { label: "Por qué elegirme", href: "#manifiesto" },
  { label: "Mis trabajos", href: "#mis-trabajos" },
  { label: "Opiniones", href: "#opiniones" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 inset-x-0 z-50 bg-white border-b border-black/5 shadow-[0_1px_20px_rgba(10,31,16,0.05)]"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-12">
        <div className="flex items-center justify-between h-[68px] md:h-[84px]">
          <a href="#top" data-testid="logo-link" className="flex items-center gap-3 shrink-0">
            <img src={LOGO} alt="Fontaneros en Huelva" className="h-10 w-10 md:h-12 md:w-12 object-contain" />
            <img src={LOGO_TEXT} alt="Instalaciones de fontanería · Alejandro García" className="h-9 md:h-11 w-auto object-contain" />
          </a>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                data-testid={`nav-${n.href.slice(1)}`}
                className="text-sm font-semibold text-[#0A1F10] hover:text-[#5AB82D] transition-colors relative group"
              >
                {n.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#5AB82D] transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={`tel:${CONTACT.phoneRaw}`}
              data-testid="header-call-btn"
              className="inline-flex items-center gap-2 rounded-full border border-[#0A1F10]/15 px-4 py-2.5 text-sm font-bold text-[#0A1F10] hover:border-[#5AB82D] hover:text-[#5AB82D] transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden xl:inline">722 29 49 23</span>
              <span className="xl:hidden">Llamar</span>
            </a>
            <motion.a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-btn"
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 rounded-full bg-[#5AB82D] px-5 py-2.5 text-sm font-bold text-white shadow-[0_8px_24px_rgba(90,184,45,0.35)] hover:bg-[#4fa526] transition-colors"
            >
              <WhatsAppIcon className="w-4 h-4" />
              WhatsApp
            </motion.a>
          </div>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-black/10 bg-white/70 backdrop-blur"
            aria-label="Menú"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden bg-white border-b border-black/5"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {NAV.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.href.slice(1)}`}
                  className="text-base font-semibold text-[#0A1F10]"
                >
                  {n.label}
                </a>
              ))}
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#5AB82D] px-5 py-3 text-sm font-bold text-white"
              >
                <WhatsAppIcon className="w-4 h-4" /> Escribir por WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
